import { RiskWorkspace } from '@app/types/workspaceTypes'
import { Button } from '@src/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@src/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@src/components/ui/popover'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@src/components/ui/tooltip'
import { cn } from '@src/lib/tailwind.utils'
import { Check, ChevronsUpDown, Folder, InfoIcon } from 'lucide-react'
import { getWorkspaceMeta, isWorkspaceSelectable } from '../utils/workspaceSelector.utils'

export type WorkspaceSelectorViewProps = {
  workspaces: RiskWorkspace[]
  selectedWorkspace: RiskWorkspace | null
  isLoading?: boolean
  isError?: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelect: (workspace: RiskWorkspace) => void
  label?: string
  placeholder?: string
  searchPlaceholder?: string
  location?: 'riskMap'
  className?: string
}

const WorkspaceSelectorView = ({
  label = '',
  placeholder = 'Select a Workspace',
  searchPlaceholder = 'Search workspaces',
  workspaces,
  selectedWorkspace,
  isLoading = false,
  isError = false,
  open,
  onOpenChange,
  onSelect,
  location,
  className,
}: WorkspaceSelectorViewProps) => {
  const triggerText = isLoading
    ? 'Loading workspaces...'
    : isError
      ? 'Unable to load workspaces'
      : (selectedWorkspace?.name ?? placeholder)

  const isRiskMap = location === 'riskMap'

  return (
    <div className={cn(location === 'riskMap' && 'min-w-0 flex-1', className)}>
      {!isRiskMap && (
        <span className="px-1 text-xs font-medium text-muted-foreground">{label}</span>
      )}
      <Popover open={open} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={isLoading || isError}
            className={cn(
              'justify-between bg-background py-1 shadow-sm',
              'data-[state=open]:border-rexBlue-500 data-[state=open]:ring-2 data-[state=open]:ring-rexBlue-500/30',
              open && 'border-rexBlue-500 ring-2 ring-rexBlue-500/30',
              isRiskMap ? 'h-9 border-1 rounded-l w-full' : 'h-14 border-2 rounded-xl w-96',
            )}
          >
            <span className="flex min-w-0 items-center gap-4 text-left">
              <span
                className={cn(
                  'h-8 w-8 flex shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground',
                  isRiskMap && 'bg-transparent w-2',
                )}
              >
                <Folder className={cn('h-4 w-4', isRiskMap && 'text-rexBlue-700')} />
              </span>
              <span className="flex min-w-0 flex-col">
                {location !== 'riskMap' && (
                  <span className="text-sm font-bold uppercase leading-none text-foreground mb-1">
                    Workspace
                  </span>
                )}
                <span className="truncate text-xs font-normal leading-none text-foreground">
                  {triggerText}
                </span>
              </span>
            </span>
            <ChevronsUpDown className="h-5 w-5 shrink-0 text-muted-foreground" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className={cn(
            'rounded-md p-2 shadow-lg',
            location === 'riskMap' ? 'w-[var(--radix-popover-trigger-width)]' : 'w-96',
          )}
          align="start"
        >
          <Command>
            <CommandInput placeholder={searchPlaceholder} className="h-10 text-sm" />
            <CommandList>
              <CommandEmpty>No workspaces found.</CommandEmpty>
              <CommandGroup className="py-2">
                <TooltipProvider delayDuration={0}>
                  {workspaces.map((workspace) => {
                    const disabled = isRiskMap ? false : !isWorkspaceSelectable(workspace)
                    const selected = selectedWorkspace?.id === workspace.id

                    return (
                      <CommandItem
                        key={workspace.id}
                        value={workspace.name}
                        disabled={disabled}
                        onSelect={() => {
                          if (disabled) return
                          onSelect(workspace)
                        }}
                        className={cn(
                          'flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 data-[disabled=true]:cursor-not-allowed data-[disabled=true]:bg-transparent data-[disabled=true]:opacity-60',
                          selected && !disabled && 'bg-muted',
                        )}
                      >
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
                          <Folder className="h-4 w-4" />
                        </span>
                        <span className="flex min-w-0 flex-1 flex-col">
                          <span className="truncate text-sm font-normal text-foreground">
                            {workspace.name}
                          </span>
                          <span className="truncate text-xs text-muted-foreground">
                            {getWorkspaceMeta(workspace)}
                          </span>
                        </span>
                        {disabled ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-sm">
                                <InfoIcon className="h-3.5 w-3.5 text-muted-foreground" />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>
                                Workspaces require a capital set, a scenario set and hazards and
                                elements.
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <Check
                            className={cn(
                              'h-4 w-4 shrink-0 text-muted-foreground',
                              selected ? 'opacity-100' : 'opacity-0',
                            )}
                          />
                        )}
                      </CommandItem>
                    )
                  })}
                </TooltipProvider>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}

export default WorkspaceSelectorView
